import React, { useState, useEffect } from "react";
import { useHistory, useParams, Link } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import { notification } from "antd";
import { HiOutlineLockClosed, HiOutlineEye, HiOutlineEyeOff } from 'react-icons/hi';
import { useTranslation } from "react-i18next";

function ResetPasswordComponent() {
  const history = useHistory();
  const { uuid: uuidParam } = useParams();
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validatingUuid, setValidatingUuid] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const validateUuid = async () => {
      try {
        setValidatingUuid(true);
        const host = window.location.host;
        const pathName = window.location.pathname.split("/")[1];

        // Use the uuid from params or fallback to current logic if needed
        const uuid = uuidParam || window.location.href.split("?")[1]?.split("/")[2];

        const response = await axiosInstance.post("pharmacy/validate_uuid", {
          uuid,
          host,
          pathname: pathName,
        });

        if (response.data.status === 1) {
          setEmail(response.data.data[0].email);
          setUserId(response.data.data[0].user_id);
        } else {
          notification.error({
            message: response.data.msg || "Invalid or expired reset link",
          });
          history.push("/forgot");
        }
      } catch (err) {
        console.error("UUID validation error:", err);
        notification.error({
          message: "Unable to validate reset link. Please try again.",
        });
        history.push("/forgot");
      } finally {
        setValidatingUuid(false);
      }
    };

    validateUuid();
  }, [uuidParam, history]);

  const validate = () => {
    const newErrors = {};
    if (!password) newErrors.password = "Password is required";
    else if (password.length < 6) newErrors.password = "Password must be at least 6 characters";

    if (!confirmPassword) newErrors.confirmPassword = "Confirm password is required";
    else if (password !== confirmPassword) newErrors.confirmPassword = "Passwords do not match";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const host = window.location.host;
      const pathName = window.location.pathname.split("/")[1];
      const uuid = uuidParam || window.location.href.split("?")[1]?.split("/")[2];

      const response = await axiosInstance.post("pharmacy/check_nd_reset_password", {
        email,
        uuid,
        password,
        user_id: userId,
        host,
        pathname: pathName,
      });

      if (response.data.status === 1) {
        notification.success({
          message: response.data.msg || "Password reset successful!",
        });
        history.push("/");
      } else {
        notification.error({
          message: response.data.msg || "Reset failed. Please try again.",
        });
      }
    } catch (err) {
      console.error("Reset password error:", err);
      notification.error({
        message: "An error occurred. Please try again later.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (validatingUuid) {
    return (
      <div className="min-h-screen bg-teal-50/50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-teal-600 mb-4"></div>
          <p className="text-teal-800 font-bold text-xl">Validating your request...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-teal-50/50 flex items-center justify-center p-4">
      <div className="max-w-[500px] w-full bg-white rounded-3xl shadow-2xl overflow-hidden border border-teal-100/40 p-8 lg:p-12">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-extrabold text-gray-900 mb-1 tracking-tight">Reset</h1>
          <p className="text-gray-500 text-base font-medium">Set your new password</p>
        </div>

        <form onSubmit={handleResetPassword} className="space-y-5">
          <div className="space-y-2">
            <label className="block text-base font-bold text-gray-700 ml-1 uppercase tracking-wider">New Password</label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <HiOutlineLockClosed className="h-6 w-6 text-gray-400 group-focus-within:text-teal-600 transition-colors" />
              </div>
              <input
                type={showPassword ? "text" : "password"}
                className={`block w-full pl-12 pr-12 py-3.5 border-2 ${errors.password ? 'border-red-500 bg-red-50/10' : 'border-gray-200 bg-gray-50/50 group-hover:border-gray-300'} rounded-2xl focus:ring-4 focus:ring-teal-500/10 focus:border-teal-500 focus:bg-white transition-all duration-300 outline-none text-gray-900 text-lg font-medium shadow-sm`}
                placeholder="••••••••"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password) setErrors(prev => ({ ...prev, password: "" }));
                }}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-teal-600 transition-colors"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <HiOutlineEyeOff size={24} /> : <HiOutlineEye size={24} />}
              </button>
            </div>
            {errors.password && <p className="mt-1 text-base text-red-500 font-semibold ml-2">{errors.password}</p>}
          </div>

          <div className="space-y-2">
            <label className="block text-base font-bold text-gray-700 ml-1 uppercase tracking-wider">Confirm Password</label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <HiOutlineLockClosed className="h-6 w-6 text-gray-400 group-focus-within:text-teal-600 transition-colors" />
              </div>
              <input
                type={showConfirmPassword ? "text" : "password"}
                className={`block w-full pl-12 pr-12 py-3.5 border-2 ${errors.confirmPassword ? 'border-red-500 bg-red-50/10' : 'border-gray-200 bg-gray-50/50 group-hover:border-gray-300'} rounded-2xl focus:ring-4 focus:ring-teal-500/10 focus:border-teal-500 focus:bg-white transition-all duration-300 outline-none text-gray-900 text-lg font-medium shadow-sm`}
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (errors.confirmPassword) setErrors(prev => ({ ...prev, confirmPassword: "" }));
                }}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-teal-600 transition-colors"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <HiOutlineEyeOff size={24} /> : <HiOutlineEye size={24} />}
              </button>
            </div>
            {errors.confirmPassword && <p className="mt-1 text-base text-red-500 font-semibold ml-2">{errors.confirmPassword}</p>}
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={loading}
              className={`w-full py-4 px-8 rounded-2xl text-white font-bold text-lg shadow-lg shadow-teal-600/30 transform transition-all active:scale-[0.98] ${loading
                ? 'bg-gray-400 cursor-not-allowed shadow-none'
                : 'bg-teal-600 hover:bg-teal-700 hover:shadow-teal-600/50'
                }`}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-6 w-6 text-white" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Updating...
                </span>
              ) : "Reset Password"}
            </button>
          </div>
        </form>

        <div className="mt-8 pt-6 border-t border-gray-100 italic text-center">
          <p className="text-gray-600 font-semibold text-base">
            Remember your password? <Link to="/" className="text-teal-600 font-bold hover:text-teal-700 transition-colors hover:underline">Back to Login</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordComponent;
